import { createAsyncThunk } from '@reduxjs/toolkit';
import WhatsAppService from '@/services/WhatsAppService';
import MessengerService from '@/services/MessengerService';
import WhatsAppCloudApiService from '@/services/WhatsAppCloudApiService';
import { WHATSAPP_OPERATIONS } from '@/services/metricsOperations';
import reportWhatsappFlowError from '@/services/reportWhatsappFlowError';
import { setWhatsappLinkedData } from '@/store/slices/components/chat/MessengerSlice';

const handler = (name, asyncFunction, operation, contextPrefix) =>
  createAsyncThunk(`WhatsApp/${name}`, async (arg) => {
    try {
      return await asyncFunction(arg);
    } catch (error) {
      reportWhatsappFlowError(error, operation, contextPrefix || name);
      throw error;
    }
  });

export const getWhatsAppSlots = handler(
  'getWhatsAppSlots',
  WhatsAppService.getWhatsAppSlots,
  WHATSAPP_OPERATIONS.GET_WHATSAPP_SLOTS,
  'whatsapp_slots'
);

export const getAvailableSlot = handler(
  'getAvailableSlot',
  WhatsAppService.getAvailableSlot,
  WHATSAPP_OPERATIONS.GET_AVAILABLE_SLOT,
  'whatsapp_slots_assignments_available'
);

export const createWhatsAppSlot = createAsyncThunk(
  'WhatsApp/createWhatsAppSlot',
  async (slotData, { dispatch }) => {
    try {
      const response = await WhatsAppService.createWhatsAppSlot(slotData);
      await dispatch(getAvailableSlot());
      return response;
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.CREATE_WHATSAPP_SLOT,
        'whatsapp_slots'
      );
      throw error;
    }
  }
);

export const whatsAppNumberLink = createAsyncThunk(
  'WhatsApp/whatsAppNumberLink',
  async ({ id, isLinked }) => {
    try {
      if (isLinked) {
        return { id, isLinked: true };
      }

      const response = await WhatsAppService.unlinkWhatsAppSlot(id);
      return {
        slot: response.slot,
      };
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.UNLINK_WHATSAPP_SLOT,
        'whatsapp_slots_instance'
      );
      throw error;
    }
  }
);

export const assignWhatsAppNumber = createAsyncThunk(
  'WhatsApp/assignWhatsAppNumber',
  async (assignmentData, { dispatch }) => {
    try {
      const response = await WhatsAppService.assignWhatsAppSlot(assignmentData);
      await dispatch(getAvailableSlot());
      return {
        slot: response.slot,
        assignment_status: response.assignment_status,
      };
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.ASSIGN_WHATSAPP_SLOT,
        'whatsapp_slots_assignments'
      );
      throw error;
    }
  }
);

export const unassignWhatsAppNumber = createAsyncThunk(
  'WhatsApp/unassignWhatsAppNumber',
  async (slotId, { dispatch }) => {
    try {
      const response = await WhatsAppService.unassignWhatsAppSlot(slotId);
      await dispatch(getAvailableSlot());
      return {
        slot: response.slot,
        assignment_status: response.assignment_status,
      };
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.UNASSIGN_WHATSAPP_SLOT,
        'whatsapp_slots_assignments'
      );
      throw error;
    }
  }
);

export const getWhatsAppQRCode = handler(
  'getWhatsAppQRCode',
  WhatsAppService.getWhatsAppQRCode,
  WHATSAPP_OPERATIONS.GET_WHATSAPP_QRCODE,
  'whatsapp_slots_qrcode'
);

export const getCloudApiPhoneNumbers = handler(
  'getCloudApiPhoneNumbers',
  WhatsAppCloudApiService.getPhoneNumbers,
  WHATSAPP_OPERATIONS.GET_CLOUD_API_PHONE_NUMBERS,
  'whatsapp_cloudapi_phone_numbers'
);

// Estado de la conexión Cloud API (WABA conectada + números con su estado de
// registración). Se consume desde el dashboard vía extraReducers.
export const getCloudApiConnectionStatus = handler(
  'getCloudApiConnectionStatus',
  WhatsAppCloudApiService.getConnectionStatus,
  WHATSAPP_OPERATIONS.GET_CLOUD_API_CONNECTION_STATUS,
  'whatsapp_cloudapi_status'
);

// Unlink de la cuenta WABA; al terminar refresca el estado de conexión.
export const unlinkCloudApiAccount = createAsyncThunk(
  'WhatsApp/unlinkCloudApiAccount',
  async (_, { dispatch }) => {
    try {
      const response = await WhatsAppCloudApiService.disconnectAccount();
      await dispatch(getCloudApiConnectionStatus());
      return response;
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.UNLINK_CLOUD_API_ACCOUNT,
        'whatsapp_cloudapi_disconnect'
      );
      throw error;
    }
  }
);

export const linkCloudApiNumber = createAsyncThunk(
  'WhatsApp/linkCloudApiNumber',
  async ({ slotId, phoneNumber }, { dispatch }) => {
    try {
      const response = await WhatsAppService.linkCloudApiNumber({
        slotId,
        phoneNumber,
      });
      await dispatch(getWhatsAppSlots());
      return {
        slot: response.slot,
        phoneNumber,
      };
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.LINK_CLOUD_API_NUMBER,
        'whatsapp_slots_instance'
      );
      throw error;
    }
  }
);

export const getWhatsAppSlot = handler(
  'getWhatsAppSlot',
  WhatsAppService.getWhatsAppSlot,
  WHATSAPP_OPERATIONS.GET_WHATSAPP_SLOT,
  'whatsapp_slots'
);

export const getAvailableBranches = handler(
  'getAvailableBranches',
  WhatsAppService.getAvailableBranches,
  WHATSAPP_OPERATIONS.GET_AVAILABLE_BRANCHES,
  'whatsapp_slots_availables_branches'
);

/**
 * Obtiene los datos de whatsappLinkedData y los guarda en MessengerSlice
 * Necesario para el dashboard cuando se muestra el error de número duplicado
 */
export const fetchWhatsappLinkedData = createAsyncThunk(
  'WhatsApp/fetchWhatsappLinkedData',
  async (_, { dispatch }) => {
    try {
      const response = await MessengerService.getWhatsappIsLinked();
      dispatch(setWhatsappLinkedData(response));
      return response;
    } catch (error) {
      reportWhatsappFlowError(
        error,
        WHATSAPP_OPERATIONS.GET_WHATSAPP_IS_LINKED,
        'whatsapp_is_linked'
      );
      throw error;
    }
  }
);
